import * as Api from '@ViewModels';
import React from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import { createRichContentEditorStateWithText } from '../../../../../models/UiUtils';
import { remapSurveyFormIds } from '../../shared/components/AdditionalQuestions/utils/surveyFormUtils';
import {
	denormalizeSurveyFormFromFields,
	isV2SurveyForm,
} from '../../shared/components/QuestionsWithSections/utils/transform';
import { CustomSurveyFormData } from '../utils';

interface UseCustomSurveyFormInitializationProps {
	surveyData?: Api.ICustomSurvey;
	isClone: boolean;
	setValue: UseFormSetValue<CustomSurveyFormData>;
}

// Helper function to calculate updateExistingContacts state from field values
const calculateUpdateExistingContacts = (surveyData?: Api.ICustomSurvey): boolean => {
	if (!surveyData) return false; // Default for new surveys

	// If any contact field has updateExisting true, return true
	return (
		surveyData.options?.contactNameOptions?.updateExisting ||
		surveyData.options?.contactEmailOptions?.updateExisting ||
		surveyData.options?.contactPhoneNumberOptions?.updateExisting ||
		surveyData.options?.contactBirthdayOptions?.updateExisting ||
		surveyData.options?.contactAddressOptions?.updateExisting ||
		false
	);
};

// Hook for form initialization and cloning logic
export const useCustomSurveyFormInitialization = ({
	surveyData,
	isClone,
	setValue,
}: UseCustomSurveyFormInitializationProps) => {
	// Gate re-initialization on survey ID — but ONLY for V2 (sectioned) surveys.
	// After a save, react-query refetches and `surveyData` gets a new reference (same
	// ID), re-firing this effect. For V2 we must NOT re-run the setValue chain: it would
	// clobber the sectioned editor's in-flight state (re-applying `surveyForm` via
	// `denormalizeSurveyFormFromFields`). For V1 we intentionally DO re-run — re-applying
	// `customForm.fields` from the refetch carries the server `_type` that flips
	// newly-saved questions to the saved visual state (matches pre-V2/master behavior);
	// gating V1 here regressed that. Sentinel for un-persisted surveys (no id).
	const hasInitializedForSurvey = React.useRef<string | null>(null);

	React.useEffect(() => {
		if (surveyData) {
			const surveyKey = surveyData.id ?? '__new__';
			if (isV2SurveyForm(surveyData.surveyForm) && hasInitializedForSurvey.current === surveyKey) return;
			hasInitializedForSurvey.current = surveyKey;

			// Use shouldDirty: false to prevent marking form as dirty during initialization
			setValue('name', isClone ? `${surveyData.name} Copy` : surveyData.name, { shouldDirty: false });
			setValue('customForm.name', isClone ? `${surveyData.name} Copy` : surveyData.name, { shouldDirty: false });
			setValue('startDate', surveyData?.startDate, { shouldDirty: false });

			// Set hasDateRange if it exists
			if (surveyData.hasDateRange !== undefined) {
				setValue('hasDateRange', surveyData.hasDateRange, { shouldDirty: false });
			}

			// Calculate updateExistingContacts state from actual field values
			const updateExistingContacts = calculateUpdateExistingContacts(surveyData);
			setValue('updateExistingContacts', updateExistingContacts, { shouldDirty: false });

			// Hydrate the partial-submission toggle from the saved survey. Without this the
			// field falls back to its static default (false) on every reload, even after save.
			setValue('allowPartialSubmission', surveyData.allowPartialSubmission ?? false, { shouldDirty: false });

			setValue(
				'options',
				{
					contactNameOptions: surveyData.options?.contactNameOptions,
					contactEmailOptions: surveyData.options?.contactEmailOptions,
					contactPhoneNumberOptions: surveyData.options?.contactPhoneNumberOptions,
					contactBirthdayOptions: surveyData.options?.contactBirthdayOptions,
					contactAddressOptions: surveyData.options?.contactAddressOptions,
					intro: createRichContentEditorStateWithText(surveyData.options?.intro?.document),
					sendConfirmationMessage: surveyData.options?.sendConfirmationMessage,
					includeCompanyLogo: surveyData.options?.includeCompanyLogo,
					showSurveyName: surveyData.options?.showSurveyName ?? true,
					companyName: surveyData.options?.companyName || '',
					confirmationMessage: createRichContentEditorStateWithText(surveyData.options?.confirmationMessage?.document),
				},
				{ shouldDirty: false }
			);

			if (surveyData?.notificationSettings) {
				setValue(
					'notificationSettings',
					{
						_type: 'SurveyNotificationSettings',
						isEnabled: surveyData.notificationSettings.isEnabled,
						additionalRecipients: surveyData.notificationSettings.additionalRecipients,
					},
					{ shouldDirty: false }
				);
			}

			if (surveyData.expirationDate) {
				setValue('expirationDate', surveyData.expirationDate, { shouldDirty: false });
			}

			if (surveyData.customForm?.fields) {
				if (isClone) {
					// Clone fields with new IDs
					const fieldIdMap = new Map<string, string>();
					const optionIdMap = new Map<string, string>();
					const cloneFields = surveyData.customForm.fields.map(field => {
						const newFieldId = uuid();
						fieldIdMap.set(field.id, newFieldId);
						return {
							...field,
							id: newFieldId,
							name: newFieldId, // Generate new name field for response key isolation
							isHidden: field.isHidden,
							isOptional: field.isOptional,
							label: field.label,
							fieldType: field.fieldType,
							options:
								field.options?.map(option => {
									const newOptionId = uuid();
									if (option.id) {
										optionIdMap.set(option.id, newOptionId);
									}
									return {
										...option,
										id: newOptionId,
										fieldId: newFieldId,
									};
								}) || [],
						};
					});
					setValue('customForm.fields', cloneFields, { shouldDirty: false });

					// Update tagGenerationSettings with new field IDs for clones
					if (surveyData.tagGenerationSettings) {
						setValue(
							'tagGenerationSettings',
							{
								tagOnCompletion: surveyData.tagGenerationSettings.tagOnCompletion || false,
								completionTag: surveyData.tagGenerationSettings.completionTag || '',
								customFormFieldTagSettings:
									surveyData.tagGenerationSettings.customFormFieldTagSettings?.map(tagSetting => ({
										...tagSetting,
										fieldId: fieldIdMap.get(tagSetting.fieldId) || tagSetting.fieldId,
									})) || [],
								effectiveCompletionTag: surveyData.tagGenerationSettings.effectiveCompletionTag || undefined,
							},
							{ shouldDirty: false }
						);
					}

					// Update customFieldMap with new field IDs for clones
					if (surveyData.customFieldMap) {
						setValue(
							'customFieldMap',
							surveyData.customFieldMap.map(mapping => ({
								...mapping,
								fieldId: fieldIdMap.get(mapping.fieldId) || mapping.fieldId,
							})),
							{ shouldDirty: false }
						);
					}

					// Update surveyForm with new field and option IDs for clones, then
					// re-hydrate each V2 question's label/options/flags from cloneFields
					// (which already has remapped IDs matching the remapped questions).
					// V1 parts (no SurveySection) pass through denormalize untouched.
					if (surveyData.surveyForm) {
						const remapped = remapSurveyFormIds(surveyData.surveyForm, fieldIdMap, optionIdMap);
						setValue('surveyForm', denormalizeSurveyFormFromFields(remapped, cloneFields), {
							shouldDirty: false,
						});
					}
				} else {
					// When not cloning, use original data as-is but ensure type safety
					setValue(
						'customForm.fields',
						surveyData.customForm.fields.map(field => ({
							...field,
							id: field.id || uuid(), // Ensure ID exists
							label: field.label || '',
							isHidden: field.isHidden || false,
							isOptional: field.isOptional || false,
							options: field.options || [],
						})),
						{ shouldDirty: false }
					);

					// Initialize tagGenerationSettings without field ID mapping
					if (surveyData.tagGenerationSettings) {
						setValue(
							'tagGenerationSettings',
							{
								tagOnCompletion: surveyData.tagGenerationSettings.tagOnCompletion || false,
								completionTag: surveyData.tagGenerationSettings.completionTag || '',
								customFormFieldTagSettings: surveyData.tagGenerationSettings.customFormFieldTagSettings || [],
								effectiveCompletionTag: surveyData.tagGenerationSettings.effectiveCompletionTag || undefined,
							},
							{ shouldDirty: false }
						);
					}

					// Initialize customFieldMap without field ID mapping
					if (surveyData.customFieldMap) {
						setValue('customFieldMap', surveyData.customFieldMap, { shouldDirty: false });
					}

					// Initialize surveyForm without field ID mapping. V2 surveys arrive
					// from the BE with reference-only questions (no label/options); join
					// against customForm.fields to restore the editor-canonical shape.
					// V1 surveys (no SurveySection parts) pass through unchanged.
					if (surveyData.surveyForm) {
						setValue(
							'surveyForm',
							denormalizeSurveyFormFromFields(surveyData.surveyForm, surveyData.customForm.fields),
							{ shouldDirty: false }
						);
					}
				}
			} else {
				// Detect V2 shape: surveyForm.surveyParts has at least one SurveySection.
				// In V2 mode we leave customForm.fields empty; fields are rebuilt from
				// surveyParts at save time. Only seed the legacy phantom field for V1
				// surveys that genuinely have no fields yet.
				if (!isV2SurveyForm(surveyData?.surveyForm)) {
					setValue(
						'customForm.fields',
						[
							{
								fieldType: Api.FormFieldType.String,
								id: uuid(),
								label: '',
								isHidden: false,
								isOptional: true,
								options: [],
							},
						],
						{ shouldDirty: false }
					);
				} else {
					// V2 survey with no customForm.fields yet — keep it empty.
					setValue('customForm.fields', [], { shouldDirty: false });
				}

				// Initialize tagGenerationSettings even when no fields exist
				if (surveyData.tagGenerationSettings) {
					setValue(
						'tagGenerationSettings',
						{
							tagOnCompletion: surveyData.tagGenerationSettings.tagOnCompletion || false,
							completionTag: surveyData.tagGenerationSettings.completionTag || '',
							customFormFieldTagSettings: surveyData.tagGenerationSettings.customFormFieldTagSettings || [],
							effectiveCompletionTag: surveyData.tagGenerationSettings.effectiveCompletionTag || undefined,
						},
						{ shouldDirty: false }
					);
				}
			}

			// Legacy data (e.g. pre-sections survey templates — #19137) arrives with NO
			// surveyForm; questions live only in customForm.fields. The form's defaultValues
			// seed a V2 SurveySection so brand-new surveys start V2 — but for loaded legacy
			// data that seed must NOT survive, or the V1 questions are orphaned and both the
			// editor (QuestionsWithSections) and the Preview (flattenSurveyPartsToFields)
			// render empty. Writing an empty surveyParts makes isV2 = false so the V1
			// <AdditionalQuestions> editor and the Preview fall back to customForm.fields.
			if (!surveyData.surveyForm) {
				setValue('surveyForm', { isEnabled: true, surveyParts: [] }, { shouldDirty: false });
			}
		}
	}, [surveyData, setValue, isClone]);
};
